import { useEffect } from "react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { Kbd } from "@/components/ui/kbd";
import { ESCAPE_PRIORITY, registerEscapeHandler } from "@/lib/escape-stack";
import {
  SHORTCUTS,
  type ShortcutDef,
  type ShortcutGroup,
} from "@/lib/shortcuts";

interface ShortcutsHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GROUP_LABELS: Record<ShortcutGroup, string> = {
  navigation: "Navigation",
  search: "Search",
  global: "Global",
  actions: "Actions",
  help: "Help",
  "detail-nav": "Detail nav",
};

const GROUP_ORDER: ShortcutGroup[] = [
  "navigation",
  "search",
  "global",
  "actions",
  "detail-nav",
  "help",
];

/**
 * Searchable shortcuts reference — opens with `?` from anywhere in the app.
 * Built on the same `CommandDialog` primitive as the command palette so
 * keyboard navigation (arrow keys, Enter to dismiss, Escape to close) feels
 * identical.
 */
export function ShortcutsHelp({ open, onOpenChange }: ShortcutsHelpProps) {
  // Register at modal priority so a single Escape closes the help panel.
  useEffect(() => {
    if (!open) return;
    return registerEscapeHandler(() => {
      onOpenChange(false);
      return true;
    }, ESCAPE_PRIORITY.modal);
  }, [open, onOpenChange]);

  const grouped = GROUP_ORDER.map((g) => ({
    group: g,
    items: SHORTCUTS.filter((s) => s.group === g) as ShortcutDef[],
  })).filter((entry) => entry.items.length > 0);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search keyboard shortcuts…" />
      <CommandList>
        <CommandEmpty>No shortcuts match.</CommandEmpty>
        {grouped.map(({ group, items }) => (
          <CommandGroup key={group} heading={GROUP_LABELS[group]}>
            {items.map((s) => (
              <CommandItem
                key={s.id}
                value={`${s.label} ${s.keywords?.join(" ") ?? ""}`}
                // Selecting an entry just dismisses the panel; the user can
                // then press the actual shortcut. We avoid binding actions
                // here so the help panel stays a discoverability surface, not
                // a duplicate command palette.
                onSelect={() => onOpenChange(false)}
              >
                <span>{s.label}</span>
                <CommandShortcut>
                  <Kbd>{s.compact}</Kbd>
                </CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
